import { ICON_BASE_PATH } from "../../../../utils/config";

import {
  TextBox,
  type TextBoxChangeEvent,
} from "@progress/kendo-react-inputs";

import { t } from "i18next";
import React, { useEffect, useState } from "react";

// Widely used sanitize presets. Each maps to the set of characters allowed
// while typing. Pick one via the `sanitizeVariant` prop, or pass a fully
// custom `pattern` to override the charset entirely.
type SanitizeVariant =
  | "titleCaseWords" // letters/numbers/space, forced Title Case (existing default behavior)
  | "alphaNumericSpace" // letters/numbers/space, case preserved as typed
  | "alphaNumeric" // letters/numbers only, no spaces, case preserved
  | "freeText" // letters/space only, case preserved (e.g. names, remarks)
  | "none"; // no charset filtering at all (only trimming/space collapsing rules below apply)

const VARIANT_PATTERNS: Record<SanitizeVariant, RegExp | null> = {
  titleCaseWords: /[^\p{L}\p{N}\s]/gu,
  alphaNumericSpace: /[^\p{L}\p{N}\s]/gu,
  alphaNumeric: /[^\p{L}\p{N}]/gu,
  freeText: /[^\p{L}\s]/gu,
  none: null,
};

interface GxTextBoxProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  validationMessage?: string;
  showStarLabel?: boolean;
  max?: number;
  minLength?: number;
  disable?: boolean;
  clearErrors?: boolean;
  iconSrc?: boolean;
  required?: boolean;
  triggerValidation?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  /** Choose which built-in charset/casing preset to apply. Defaults to the
   * original "titleCaseWords" behavior so existing usages are unaffected. */
  sanitizeVariant?: SanitizeVariant;
  /** Fully custom allowed-character regex (characters matching this pattern
   * are stripped). Overrides `sanitizeVariant`'s charset when provided. */
  pattern?: RegExp;
  /** When true, skips forcing Title Case so users can type in caps / as-is.
   * Ignored for the "titleCaseWords" variant unless explicitly set true. */
  preserveCase?: boolean;
}

const GxTextBox: React.FC<GxTextBoxProps> = ({
  label,
  value,
  onChange,
  validationMessage,
  showStarLabel = true,
  max,
  disable,
  iconSrc,
  required = false,
  triggerValidation,
  clickable = false,
  onClick,
  sanitizeVariant = "titleCaseWords",
  pattern,
  preserveCase,
}) => {
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);

  const shouldTitleCase =
    preserveCase === true
      ? false
      : preserveCase === false
      ? true
      : sanitizeVariant === "titleCaseWords";

  const sanitize = (s: string) => {
    const charsetPattern = pattern ?? VARIANT_PATTERNS[sanitizeVariant];

    let cleaned = charsetPattern ? s.replace(charsetPattern, "") : s;
    // Collapse repeated spaces only; trailing/leading trim happens on blur
    // (see handleBlur) so users can still type a trailing space mid-word.
    cleaned = cleaned.replace(/\s+/g, " ");

    if (!shouldTitleCase) {
      return cleaned;
    }

    return cleaned.replace(/\b(\p{L})(\p{L}*)/gu, (_, first, rest) => {
      return first.toUpperCase() + (rest ? rest.toLowerCase() : "");
    });
  };

  const validate = (val: string) => {
    if (required && !val.trim()) {
      return t("group_name_required");
    }
    return "";
  };

  const handleChange = (e: TextBoxChangeEvent) => {
    if (clickable) return; // Prevent changes when clickable is true

    let raw = String(e.value ?? "");
    let filtered = sanitize(raw);

    if (max && filtered.length > max) {
      filtered = filtered.slice(0, max);
    }

    onChange(filtered);

    if (touched) {
      setError(validate(filtered));
    }
  };

  const handleFocus = () => {
    if (clickable) return; // Prevent focus when clickable is true
    setFocused(true);
  };

  const handleBlur = () => {
    if (clickable) return; // Prevent blur when clickable is true
    setFocused(false);
    setTouched(true);

    const trimmed = value.trim();
    if (trimmed !== value) {
      onChange(trimmed);
    }

    setError(validate(trimmed));
  };

  const handleContainerClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      setError(validate(value));
    }
  }, [triggerValidation]);

  useEffect(() => {
    if (validationMessage) {
      setError(validationMessage);
      setTouched(true);
    } else if (touched) {
      setError(validate(value));
    }
  }, [validationMessage]);

  const displayValidationMessage = touched ? error : "";

  return (
    <div className="gx_input_container">
      <label
        className={`input-label gx-font-semibold ${
          focused || value ? "floating" : ""
        }`}
      >
        {label}
        {showStarLabel && (
          <span
            className={`gx_input_star ${
              focused || value
                ? "gx_input_star_float"
                : "gx_input_star_normal"
            }`}
          >
            *
          </span>
        )}
      </label>

      <div
        className={`gx_input_cont ${
          displayValidationMessage ? "invalid" : "valid"
        } ${clickable ? "clickable" : ""}`}
        onClick={handleContainerClick}
        style={{
          cursor: clickable ? "pointer" : "default",
        }}
      >
        {iconSrc && (
          <img
            src={`${ICON_BASE_PATH}required-icon.svg`}
            alt="icon"
            className="gx-salik_required_img"
          />
        )}

        <TextBox
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          type="text"
          autoComplete="off"
          disabled={disable || clickable}
          max={max}
          maxLength={200}
          className={`gx-textbox-css ${clickable ? "clickable-textbox" : ""}`}
          aria-invalid={!!displayValidationMessage}
          style={{
            cursor: clickable ? "pointer" : "text",
            pointerEvents: clickable ? "none" : "auto",
          }}
        />
      </div>

      {displayValidationMessage && (
        <div className="gx_input_validationmessage gx-font-regular">
          {displayValidationMessage}
        </div>
      )}
    </div>
  );
};

export default GxTextBox;